<template>
  <div
    :class="[
      $style.productContainer,
      {[$style.productContainerHighlighting]: highlighting}
    ]"
  >
    <nuxt-link v-if="!productUrlDisabled" :to="productUrl(getVariant)" class="awSrOnlyAbs">
      <component :is="headingTag">
        {{ getVariantName }}
      </component>
    </nuxt-link>
    <div v-if="isUncreditable" class="awSrOnlyAbs" v-text="$awt('aw.common.basket_product_list.uncreditable_warning')" />
    <!-- VOUCHER INFO region -->
    <div v-if="getVoucherForCartItem && !isReplacementItem" :class="$style.couponInfo">
      <aw-coupon-used-info-version2
        :target-uuid="getVariantUUID"
        :voucher-uuid="getVoucherForCartItem.uuid"
        :is-cart-page="isCartPage"
        :background="highlighting ? $styleVariables.colorMarigold30012 : $styleVariables.colorBackground1"
      />
    </div>
    <!-- VOUCHER INFO region -->
    <div :class="$style.productWrapper">
      <!-- PRODUCT INFO region -->
      <div :class="$style.productInfoWrapper">
        <div :class="$style.productImage">
          <lv-image :sources="image" sizing="cover" auto-height />
        </div>
        <div :class="$style.productInfo">
          <div :class="$style.productName">
            <nuxt-link
              v-if="!productUrlDisabled"
              :to="productUrl(getVariant)"
              aria-hidden="true"
              tabindex="-1"
            >
              <component :is="headingTag">
                <div :class="$style.productNameHeading">
                  {{ getVariantName }}
                </div>
              </component>
            </nuxt-link>
            <span v-else>{{ getVariantName }}</span>
            <div v-if="isError" :class="[$style.errorMessage]">
              <lv-icon :class="[$style.errorIcon]" name="exclamation-triangle-24" :size="24" />
              <span :class="$style.errorText">
                {{ $awt('aw.common.product_list.no_delivery') }}
              </span>
            </div>
          </div>
          <span
            v-if="productPackageUnitSizeStr"
            :class="$style.productUnitSizeGridArea"
            v-text="productPackageUnitSizeStr"
          />
        </div>
      </div>
      <!-- PRODUCT INFO region -->
      <div :class="$style.productBodyWrapper">
        <div v-if="!isReplacementItem && (showCreditBlock || showStickerBlock)" :class="$style.stickerAndCreditWrapper">
          <!-- STICKER INFO region -->
          <div v-if="showStickerBlock" :class="$style.stickerWrapper">
            <span :class="$style.label" v-text="$awt('aw.common.basket_product_list.sticker')" />
            <aw-select-version2
              v-model:selected-options="selectedStickerQuantity"
              :options="stickerEnabledArray"
              :label="$awt('aw.common.basket_product_list.sticker')"
              :multiple="false"
              input-size="sm"
              :dropdown-icon-attrs="{
                name: 'arrow-down-16',
                size: 14
              }"
              :dynamic-width="true"
              :dynamic-width-without-translate-x="true"
              @update:selected-options="changedStickerQuantity"
            />
          </div>
          <!-- STICKER INFO region -->
          <!-- CREDIT INFO region -->
          <div v-if="showCreditBlock" :class="$style.creditInfoWrapper">
            <span :class="$style.label" v-text="$awt('aw.common.basket_product_list.credit')" />
            <lv-tooltip styling="primary-version2" :options="{placement: 'top-start'}" :class="$style.creditInfoTooltip">
              <template #tooltip>
                <div data-popover-item-wrapper>
                  <ul :class="$style.creditInfoTooltipUl">
                    <li
                      v-for="creditType in productItem.creditTypes"
                      :key="creditType.baremId"
                      v-html="creditType.name"
                    />
                  </ul>
                </div>
              </template>
              <template #reference>
                <label :class="$style.creditInfoLabelWrapper">
                  <span :class="$style.creditInfoLabel">
                    {{
                      productItem.creditTypes?.length === 1
                        ? $awt('aw.common.basket_product_list.credit_types.interest')
                        : $awt('aw.common.basket_product_list.credit_types.several')
                    }}
                  </span>
                  <lv-icon name="education-16" :size="16" :class="$style.infoIcon" />
                </label>
              </template>
            </lv-tooltip>
          </div>
          <!-- CREDIT INFO region -->
        </div>
        <div
          :class="[
            $style.quantityPriceWrapper, {
              [$style.quantityPriceWrapperWithoutBorder]: !showSelection && isReplacementItem,
              [$style.quantityPriceWrapperReplacementItem]: isReplacementItem
            }
          ]"
        >
          <!-- QUANTITY region -->
          <div v-if="!isReplacementItem" :class="$style.quantityWrapper">
            <span :class="$style.label" v-text="$awt('aw.common.basket_product_list.quantity')" />
            <client-only>
              <aw-add-to-basket-version2
                v-if="!quantityReadOnly"
                portal-target-stock=""
                inner-class-status=""
                inner-class-cart-btn=""
                inner-class-stock=""
                :is-quantity-selector-small="false"
                :is-button-icon-visible="false"
                :is-button-text-visible="false"
                :is-stock-change-allowed="false"
                :is-cart-button-visible="false"
                :is-status-visible="false"
                status-text-type="shorterText"
                :basket-stock-type="basketStockType"
                :product-data="{
                  selectedVariant: productItem.variant
                }"
                :basket-item-uuid="getVariantUUID"
                card-size="xxs"
                input-size="md"
                is-small
                :show-loading-spinner="showLoadingSpinner"
                is-basket-page
                :is-version2="true"
                :is-quantity-selector-double-sided-small="true"
                :is-quantity-selector-double-sided="!!screenRange['tablet-max']"
                quantity-selector-direction="horizontal"
                :inner-class-quantity="$style.innerClassQuantity"
                :class="$style.quantity"
              />
              <span v-else-if="availableOnline && quantity" v-text="mappedQuantity.text" />
            </client-only>
          </div>
          <!-- QUANTITY region -->
          <!-- PRICE region -->
          <div
            v-if="showPrice"
            :class="$style.priceGrid"
          >
            <div v-if="isDiscounted" :class="$style.originalPriceValues">
              <div v-if="productPrices.secondary.main" :class="$style.originalPriceValueFull">
                <template v-if="!isUnitTrivial">
                  <span class="awSrOnlyAbs" v-text="$awt('aw.common.product_card.accessibility.regular_price')" />
                  <del :class="['delReset', $style.originalPrice]" v-html="productPrices.secondary.main" />
                </template>
              </div>
              <div v-if="productPrices.secondary.units.length" :class="$style.originalPriceValueUnits">
                <span class="awSrOnlyAbs" v-text="$awt('aw.common.product_card.accessibility.regular_unit_price')" />
                <template v-for="(p, i) in productPrices.secondary.units">
                  <del
                    v-if="p"
                    :key="i"
                    :class="['delReset', $style.originalPriceValueUnit]"
                    v-html="p"
                  />
                </template>
              </div>
            </div>
            <!-- region primaryPrice -->
            <div :class="[$style.currentPriceBox, {[$style.currentPriceBoxIsDiscounted]: isDiscounted}]">
              <div :class="$style.currentPrice">
                <span class="awSrOnlyAbs" v-text="$awt('aw.common.product_card.accessibility.price')" />
                <span :class="$style.priceGross" v-text="productPrices.primary.main" />
                <div v-if="rollInfo" :class="$style.priceRoll">
                  <lv-tooltip styling="primary-version2" :options="{placement: 'top-start'}">
                    <template #tooltip>
                      <div data-popover-item-wrapper>
                        <span
                          v-text="$awt(`aw.common.product_card.accessibility.${rollType === 'redemption' ? 'redemption_info' : 'roll_info'}`)"
                        />
                      </div>
                    </template>
                    <template #reference>
                      <span :class="$style.priceRollText">
                        +&nbsp;{{ rollInfo }}
                      </span>
                    </template>
                  </lv-tooltip>
                </div>
              </div>
              <div v-if="productPrices.primary.units.length">
                <span class="awSrOnlyAbs" v-text="$awt('aw.common.product_card.accessibility.unit_price')" />
                <span v-html="productPrices.primary.units[0]" />
              </div>
            </div>
          </div>
          <!-- PRICE region -->
        </div>
      </div>
      <!-- BUTTON region -->
      <div v-if="!isReplacementItem" :class="$style.buttonWrapper">
        <lv-button
          v-if="showDelete"
          type="button"
          styling="small-secondary-dark-just-icon"
          shrink
          :title="$awt('aw.common.product_list.item.delete_item')"
          @click="removeItem"
        >
          <lv-icon name="trash-version2-16" :size="16" />
        </lv-button>
      </div>
      <div v-if="isReplacementItem && showSelection" :class="$style.buttonWrapper">
        <aw-radio-chooser
          :value="replacementParentProductItem?.replacement?.selectedVariant?.id"
          :input-id="getVariant.id"
          :radio-group-uuid="radioGroupUuid"
          @update:model-value="$emit('replacement-product-selected', { id: getVariant.id, chosen: $event })"
        />
      </div>
      <!-- BUTTON region -->
    </div>
    <!-- REPLACEMENT region -->
    <div
      v-if="!isReplacementItem && (productReplaceIsAvailable || firstSelectedReplacementProduct)"
      :class="$style.productReplacementLinkWrapper"
    >
      <template v-if="productReplaceIsAvailable && !firstSelectedReplacementProduct">
        {{ $awt('aw.common.basket_product_list.replacement.title') }}
        <lv-button
          type="button"
          styling="link-primary-version2"
          shrink
          :class="$style.productReplacementLinkBtn"
          @click="openReplacementModal()"
        >
          {{ $awt('aw.common.basket_product_list.replacement.button_text') }}
        </lv-button>
      </template>
      <template v-if="firstSelectedReplacementProduct">
        <div>
          <strong>{{ $awt('aw.common.basket_product_list.replacement.product') }}</strong>
          {{ firstSelectedReplacementProduct?.replacement?.selectedVariant?.name }}
        </div>
        <lv-button
          type="button"
          styling="link-primary-version2"
          shrink
          :class="$style.productReplacementLinkBtn"
          @click="openReplacementModal()"
        >
          {{ $awt('aw.common.basket_product_list.replacement.edit_button_text') }}
        </lv-button>
      </template>
      <aw-modal
        v-if="!isReplacementItem"
        :opened="modal.type === replacementModalType"
        size="md"
        :modal-title="$awt('aw.common.basket_product_list.replacement.modal.title')"
        @close-modal="hideReplacementModal"
      >
        <template #content>
          <aw-separator margin-top="none" />
          <aw-loading :is-loading="selectionLoading">
            <div :class="$style.productReplacementModalContent">
              <div :class="$style.productReplacementModalContentActualProduct">
                <span :class="$style.productReplacementModalContentDesc">{{ $awt('aw.common.basket_product_list.replacement.modal.product_label') }}</span>
                <aw-cart-product-list-item
                  :product-item="productItem"
                  :is-replacement-item="true"
                  :background="$styleVariables.colorBackground2"
                />
              </div>
              <div :class="$style.productReplacementModalContentReplacements">
                <span :class="$style.productReplacementModalContentDesc">{{ $awt('aw.common.basket_product_list.replacement.modal.replacement_product_label') }}</span>
                <aw-cart-product-list-item
                  v-for="product in replacedItems"
                  :key="product.id"
                  :product-item="product"
                  :replacement-parent-product-item="productItem"
                  :is-replacement-item="true"
                  :show-selection="true"
                  :background="$styleVariables.colorBackground4"
                  :border="`1px solid ${$styleVariables.colorBorderDisabled}`"
                  :radio-group-uuid="radioGroupKey"
                  :highlighting="productItem?.replacement?.selectedVariant?.id === product?.variant?.id "
                  @replacement-product-selected="handleReplacementProductSelection"
                />
              </div>
            </div>
          </aw-loading>
          <aw-separator />
          <div :class="$style.productReplacementModalButtonWrapper">
            <lv-button
              type="button"
              styling="primary-dark"
              shrink
              :title="$awt('aw.common.basket_product_list.replacement.modal.ok_button')"
              @click="hideReplacementModal"
            >
              {{ $awt('aw.common.basket_product_list.replacement.modal.ok_button') }}
            </lv-button>
          </div>
        </template>
      </aw-modal>
    </div>
    <!-- REPLACEMENT region -->
    <!-- OLD_PRODUCT region -->
    <label
      v-if="canReturn && !isReplacementItem"
      :class="$style.productOldProductDeliveryWrapper"
    >
      {{ $awt('aw.common.basket_product_list.old_product_delivery') }}
      <aw-toggle-version2 v-model="usedItemReturn" @update:model-value="setReturnUsedItem" />
    </label>
    <!-- OLD_PRODUCT region -->
  </div>
</template>

<script>
  import { defineAsyncComponent, nextTick } from 'vue';
  import { mapState } from 'pinia';
  import { useNuxtApp, useRoute, useRuntimeConfig } from 'nuxt/app';
  import { epsRound, uuid4, webpFallback } from '~~/common/utils';
  import { productPackageUnitSizeStr } from '~~/common/utils/variant';
  import { LvButton, LvHeading, LvIcon, LvImage, LvTooltip } from '~~/common/components/loginet-vue-shop/index';
  import AwSpinner from '~~/common/components/Common/AwSpinner';
  import { CBDS_NOT_AVAILABLE_ONLINE } from '~~/common/config/CartButtonDisplayStatus';
  import { M_CART_REPLACEMENT_PRODUCTS, M_PROD_OLD } from '~~/common/config/Modal';
  import { setErrorNotification } from '~~/common/utils/helper';

  import urlMixin from '~~/common/mixins/urlMixin';
  import productDataMixin from '~~/common/mixins/productDataMixin';

  import { useModalStore } from '~~/common/stores/modal';
  import { useOrderStore } from '~~/common/stores/order';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useProductStore } from '~~/common/stores/product';
  import { useLoyaltyCardStore } from '~~/shop/app/stores/loyaltyCard';
  import { determinePriceType } from '~~/common/utils/productPriceType.js';
  import AwToggleVersion2 from '~~/common/components/Common/Widgets/AwToggleVersion2.vue';
  import AwModal from '~~/common/components/Common/AwModal';
  import AwRadioChooser from '~~/common/components/Common/Widgets/AwRadioChooser.vue';
  import AwSeparator from '~~/common/components/Common/AwSeparator.vue';
  import { PUSH_REMOVE_FROM_CART } from '~~/common/plugins/aw-analytics.js';


  export default {
    name: 'AwCartProductListItem',
    components: {
      AwSeparator,
      AwRadioChooser,
      AwModal,
      AwToggleVersion2,
      AwLoading: defineAsyncComponent(() => import('~~/common/components/Common/AwLoading.vue')),
      AwCouponUsedInfoVersion2: defineAsyncComponent(() => import('~~/common/components/Common/BasketProductList/AwCouponUsedInfoVersion2')),
      AwSpinner,
      AwAddToBasketVersion2: defineAsyncComponent(() => import('~~/common/components/Page/Common/BasketControl/AwAddToBasketVersion2')),
      LvButton,
      LvImage,
      LvIcon,
      LvHeading,
      LvTooltip,
      AwSelectVersion2: defineAsyncComponent(() => import('~~/common/components/Common/AwSelectVersion2')),
    },
    mixins: [
      urlMixin,
      productDataMixin,
    ],
    props: {
      headingTag: {
        type: String,
        default: 'span',
      },
      checkoutType: {
        type: String,
        default: '',
      },
      productItem: {
        type: Object,
        required: true,
      },
      isReplacementItem: {
        type: Boolean,
        default: false,
      },
      background: {
        type: String,
        default: 'transparent',
      },
      border: {
        type: String,
        default: 'none',
      },
      showSelection: {
        type: Boolean,
        default: false,
      },
      radioGroupUuid: {
        type: String,
        default: null,
      },
      replacementParentProductItem: {
        type: Object,
        required: false,
        default: null,
      },
      highlighting: {
        type: Boolean,
        default: false,
      },
      quantity: {
        type: Object,
        default: null,
      },
      showDelete: {
        type: Boolean,
        default: true,
      },
      quantityReadOnly: {
        type: Boolean,
        default: false,
      },
      isError: {
        type: Boolean,
        default: false,
      },
      showPrice: {
        type: Boolean,
        default: true,
      },
      productUrlDisabled: {
        type: Boolean,
        default: false,
      },
      basketStockType: {
        type: String,
        default: null,
      },
      showLoadingSpinner: {
        type: Boolean,
        default: true,
      },
      isUncreditable: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['remove-item', 'select-item', 'replacement-product-selected'],
    data () {
      return {
        currentStepIndex: null,
        stickerEnabledArray: [],
        oldProductModalType: M_PROD_OLD + uuid4(),
        replacementModalType: M_CART_REPLACEMENT_PRODUCTS + uuid4(),
        usedItemReturn: null,
        isQuantityActive: false,
        replacedItemsLoader: false,
        replacedItems: [],
        notificationKey: 0,
        selectedStickerQuantity: {
          id: -1,
          name: 0,
        },
        radioGroupKey: uuid4(),
        selectionLoading: false,
      };
    },
    computed: {
      ...mapState(useModalStore, {
        modal: state => state.activeGenericModal,
      }),
      ...mapState(useUserInterfaceStore, {
        screenSize: state => state.mediaQueries.query,
        screenRange: state => state.mediaQueries,
      }),
      ...mapState(useLoyaltyCardStore, {
        stickers (state) {
          return state.expectedLoyaltyPoints?.stickers?.reduce((a, sticker) => ({
            ...a,
            [sticker.id]: sticker,
          }), {}) || null;
        },
      }),
      ...mapState(useOrderStore, {
        firstSelectedReplacementProduct (state) {
          if (this.checkoutType) {
            const packages = state.order?.splits?.find?.(s => s.checkoutType === this.checkoutType)?.packages || [];
            for (const splitPackage of packages) {
              const replacement = splitPackage.items.find((e) => {
                return e.replacement?.selectedVariant && e.uuid === this.getVariantUUID;
              });
              if (replacement) {
                return replacement;
              }
            }
          }
          return false;
        },
        getVoucherForCartItem (state) {
          return state.getCurrentFlattenedVouchers.find((v) => {
            return (v.appliedDiscounts || []).find(ad => ad.uuid === this.getVariantUUID);
          });
        },
      }),
      ...mapState(useProductStore, {
        isDiscounted (state) {
          return state.isPriceDiscounted(this.getVariant?.price);
        },
      }),
      productReplaceIsAvailable () {
        return this.productItem?.replacement?.available && this.checkoutType === 'FOOD';
      },
      getVariant () {
        return this.productItem.variant;
      },
      getVariantUUID () {
        return this.productItem?.uuid;
      },
      getVariantId () {
        return this.getVariant?.id;
      },
      getVariantName () {
        return this.getVariant?.name;
      },
      getProductId () {
        return this.getVariant?.productId;
      },
      price () {
        return this.$price({ gross: this.getVariant?.price?.grossDiscounted }, this.getVariant?.price?.currency);
      },
      originalPrice () {
        return this.$price({ gross: this.getVariant?.price?.gross }, this.getVariant?.price?.currency);
      },
      piecePrice () {
        return this.$price({ gross: this.getVariant?.piecePrice?.grossDiscounted }, this.getVariant?.price?.currency);
      },
      image () {
        const media = this.productItem.variant?.media || {};

        return webpFallback([
          {
            url: media?.listImage || media?.mainImage || media?.images?.[0] || '',
          },
        ]);
      },
      isLoose () {
        return this.getVariant?.loose?.loose || false;
      },
      weightPerPiece () {
        return this.getVariant?.loose?.weightPerPiece || 1;
      },
      quantityStepSize () {
        return this.getVariant?.cartInfo?.quantityStepSize;
      },
      quantitySteps () {
        return this.getVariant?.cartInfo?.quantitySteps;
      },
      availableOnline () {
        return this.getVariant?.cartInfo?.availability !== CBDS_NOT_AVAILABLE_ONLINE;
      },
      mappedQuantity () {
        let result = this.quantityArray.find(quantity => epsRound(this.quantity?.quantity) === epsRound(quantity.value));
        if (!result) {
          result = this.fallbackMappedQuantity;
        }
        return result;
      },
      fallbackMappedQuantity () {
        const value = this.quantity?.quantity;
        // Getting text
        let typeText = this.quantityArray.find(quantity => this.quantity?.type === quantity.unit);
        typeText = typeText.text.split(' ');
        typeText = typeText[typeText.length - 1];
        const text = value + ' ' + typeText;

        const unit = this.quantity?.type;

        return {
          value,
          text,
          unit,
        };
      },
      quantityArray () {
        return [...Array(this.quantitySteps).keys()]
          .map(q => (q + 1) * this.quantityStepSize / (this.isLoose && ['piece', 'db'].includes(this.quantity?.type) ? this.weightPerPiece : 1))
          .map(q => epsRound(q))
          .map(q => ({
            value: q,
            text: `${q} ${this.$awt('aw.global.quantity.' + this.quantity?.type)}`,
            unit: this.quantity?.type,
          }));
      },
      canReturn () {
        return this.productItem?.usedItem?.canReturn;
      },
      rollType () {
        return this.getVariant?.roll?.type;
      },
      rollInfo () {
        return this.getVariant?.roll?.price
          ? this.$price({ gross: this.getVariant.roll.price.grossDiscounted }, this.getVariant?.roll.price.currency)
          : null;
      },
      isCartPage () {
        const route = useRoute();
        return /.*?\/(basket|kosar)/gi.test(route.fullPath);
      },
      showStickerBlock () {
        return __AW_IS_SHOP__ && this.productItem?.stickerRedemption?.option && this.stickerEnabledArray && this.stickers?.[this.productItem?.stickerRedemption?.option?.id];
      },
      creditStatus () {
        return this.productItem?.creditStatus;
      },
      showCreditBlock () {
        const config = useRuntimeConfig();
        return this.creditStatus && config.public.cetelemShopCode;
      },
      productPackageUnitSizeStr () {
        return productPackageUnitSizeStr({
          variant: this.getVariant,
          locale: this.$i18n.locale,
        });
      },
      productPriceType () {
        return determinePriceType({
          variant: this.getVariant,
          isLoyaltyPriceValid: this.getUnCachedProduct?.isLoyaltyPriceValid,
        });
      },
    },
    watch: {
      'productItem.stickerRedemption.option.quantities': {
        handler () {
          this.initStickerEnabledObject();
        },
        deep: true,
      },
      'productItem.stickerRedemption.option': {
        handler () {
          this.setStickerEnabledObject();
        },
        deep: true,
      },
    },
    mounted () {
      this.initStickerEnabledObject();
      nextTick(() => {
        this.notificationKey++;
      });
      this.usedItemReturn = this.productItem?.usedItem?.return;
    },
    methods: {
      openReplacementModal () {
        this.getReplacementItems();
        const modalStore = useModalStore();
        modalStore.showReplacementModal({ modalType: this.replacementModalType });
      },
      hideReplacementModal () {
        const modalStore = useModalStore();
        modalStore.hideReplacementModal({ modalType: this.replacementModalType });
      },
      async getReplacementItems () {
        this.replacedItemsLoader = true;
        try {
          const result = await this.$api.$get(`/products/${this.getProductId}/variants/${this.getVariantId}/replacements?itemsPerPage=100&page=1`);
          this.replacedItems = result.results.map(product => ({
            ...product,
            variant: product.selectedVariant,
          }));
        } catch (error) {
          setErrorNotification({
            nuxtApp: useNuxtApp(),
            error,
          });
          this.openReplacementModal();
        } finally {
          this.replacedItemsLoader = false;
        }
      },
      async removeItem () {
        const orderStore = useOrderStore();
        await orderStore.removeCartItem({
          uuid: this.productItem.uuid,
          fromBasket: true,
        });
        this.$awAnalytics[PUSH_REMOVE_FROM_CART]({ name: this.productItem.variant?.name });
        orderStore.removeReturnUsedItem(this.productItem.uuid);
      },
      async setReturnUsedItem (returnUsed) {
        const modalStore = useModalStore();
        const orderStore = useOrderStore();
        await orderStore.updateCartItemReturnUsed({
          uuid: this.productItem.uuid,
          returnUsed,
          fromBasket: this.isCartPage,
        });
        orderStore.setReturnUsedItem({ [this.productItem.uuid]: returnUsed });
        modalStore.hideProdOld({ modalType: this.oldProductModalType });
      },
      initStickerEnabledObject () {
        this.stickerEnabledArray = (this.productItem?.stickerRedemption?.option?.quantities || [])
          .map((quantity, index) => {
            return ({
              id: index,
              name: quantity,
            });
          });

        this.stickerEnabledArray = [
          {
            id: -1,
            name: 0,
          },
          ...this.stickerEnabledArray,
        ];

        if (this.productItem?.stickerRedemption?.option?.quantities) {
          this.setStickerEnabledObject();
        }
      },
      setStickerEnabledObject () {
        const sticker = this.stickerEnabledArray.find(({ name }) => name === this.productItem?.stickerRedemption?.quantity);
        if (sticker) {
          this.selectedStickerQuantity = sticker;
        }
      },
      async changedStickerQuantity (newVal) {
        const orderStore = useOrderStore();
        await orderStore.updateCartItemStickerUsed({
          uuid: this.productItem.uuid,
          selectedOption: (newVal.id !== -1
            ? {
              id: this.productItem?.stickerRedemption.option.id,
              quantity: newVal.name,
            }
            : {
              id: null,
              quantity: null,
            }
          ),
          fromBasket: this.isCartPage,
        });
        this.setStickerEnabledObject();
      },
      async handleReplacementProductSelection (selected) {
        this.selectionLoading = true;
        const orderStore = useOrderStore();
        const data = {
          replacement: {
            selectedId: selected.id,
            available: true,
            option: selected.chosen ? 'chosen' : 'dontwant',
          },
        };

        await orderStore.updateCartItem({
          uuid: this.productItem?.uuid,
          data,
          fromBasket: this.isCartPage,
        }).finally(() => this.selectionLoading = false);
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">
.product {
  &Container {
    font-weight: $font-weight-normal-v2;
    line-height: 16px;
    display: flex;
    flex-direction: column;
    padding: 12px;
    color: $color-text-primary;
    border: v-bind(border);
    border-radius: 12px;
    background: v-bind(background);
    gap: 8px;

    @include tablet(min) {
      border-radius: 16px;
    }

    &Highlighting {
      border: 2px solid $color-marigold-300;
      background: $color-marigold-300--12;
    }
  }

  &Wrapper {
    display: grid;
    grid-template-areas: "productInfo button" "productBody .";
    grid-template-columns: 1fr max-content;
    align-items: stretch;
    justify-content: space-between;
    gap: 8px;

    @include tablet(min) {
      gap: 12px;
      grid-template-areas: "productInfo button" "productBody .";
      grid-template-columns: 1fr;
    }

    @include desktop-small(min) {
      grid-template-areas: "productInfo productBody button";
      grid-template-columns: 1fr max-content max-content;
      padding-right: 8px;
    }

    @include desktop-medium(min) {
      gap: 24px;
    }
  }

  &InfoWrapper {
    grid-area: productInfo;
    display: grid;
    grid-template-columns: auto auto;
    align-items: center;
    justify-content: start;
    gap: 12px;
    padding: 0 12px 0 0;

    @include desktop-small(min) {
      border-right: 1px dashed $color-dash-separator;
    }
  }

  &Info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
  }

  &Name {
    font-size: 12px;
    font-weight: $font-weight-bold-v2;
    line-height: 16px;

    @include desktop-medium(min) {
      font-size: 16px;
      line-height: 24px;
    }

    &Heading {
      @include multiline-ellipsis(2);
    }
  }

  &Image {
    width: 48px;
    height: 48px;
    padding: 8px;
    border-radius: 8px;
    background-color: $color-background-4;

    > picture {
      height: 100%;
      border-radius: 8px;
      @include desktop-medium(min) {
        border-radius: 12px;
      }
    }

    @include tablet(min) {
      width: 56px;
      height: 56px;
    }

    @include desktop-medium(min) {
      width: 72px;
      height: 72px;
      border-radius: 12px;
    }
  }

  &UnitSizeGridArea {
    font-size: 12px;
    font-weight: $font-weight-normal-v2;
    line-height: 16px;

    @include tablet(max) {
      font-size: 10px;
    }
  }

  &ReplacementLink {
    &Wrapper {
      font-size: 10px;
      line-height: 16px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: 8px 0 0;
      border-top: 1px solid $color-border-disabled;
      gap: 24px;

      @include tablet(min) {
        justify-content: start;
      }

      @include desktop-medium(min) {
        font-size: 12px;
        padding: 8px 12px 0;
      }
    }

    &Btn {
      font-size: 10px;
      line-height: 16px;
      text-decoration: underline;

      @include desktop-medium(min) {
        font-size: 12px;
      }
    }
  }

  &ReplacementModalContent {

    font-size: 16px;
    line-height: 24px;
    display: flex;
    flex-direction: column;
    gap: 40px;

    &Desc {
      padding-bottom: 12px;
    }

    &ActualProduct {
      display: flex;
      flex-direction: column;
    }

    &Replacements {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
  }

  &ReplacementModalButtonWrapper {
    display: flex;
    flex-direction: row;
    justify-content: center;
  }

  &OldProductDelivery {
    &Wrapper {
      font-size: 10px;
      line-height: 16px;
      display: flex;
      align-items: center;
      flex-direction: row;
      padding: 8px 0 0;
      cursor: pointer;
      border-top: 1px solid $color-border-disabled;
      gap: 12px;

      @include desktop-medium(min) {
        font-size: 12px;
        padding: 8px 12px 0;
      }
    }
  }
}

.priceGrid {
  grid-area: price;
  display: grid;
  grid-template-areas:
    "originalPriceArea"
    "currentPriceArea";
}

.originalPriceBox {
  grid-area: originalPriceArea;
}

.currentPriceBox {
  font-size: 10px;
  font-weight: $font-weight-normal-v2;
  line-height: 16px;
  display: inline-block;
  padding: 8px;
  color: var(--awProductCardCurrentPriceColor, currentColor);
  border-radius: 8px;
  background: var(--awProductCardCurrentPriceBg, transparent);
  grid-area: currentPriceArea;
  justify-self: start;

  &IsDiscounted {
    --awProductCardCurrentPriceColor: #{$color-white};
    --awProductCardCurrentPriceBg: #{$color-strawberry-500};
  }
}

.currentPrice {
  display: flex;
  gap: 4px;
}

.priceGross {
  font-size: 18px;
  font-weight: $font-weight-bold-v2;
  line-height: 24px;
}

.originalPrice {
  font-weight: $font-weight-bold-v2;
}

.priceRoll {
  align-self: flex-end;
  padding-bottom: 2px;
}

.priceRollText {
  font-weight: $font-weight-medium-v2;
  text-decoration-line: underline;
  cursor: help;
}

.originalPriceValueUnits {
  display: flex;
  flex-direction: column;
}

.originalPriceValues {
  font-size: 10px;
  line-height: 16px;
  position: relative;
  display: inline-flex;
  width: fit-content;
  gap: 4px;
  justify-self: end;

  .originalPriceValueUnit {
    position: relative;
  }

  .originalPriceValueFull,
  :nth-child(n + 2 of .originalPriceValueUnit) {
    &::before {
      font-size: 13px;
      position: absolute;
      top: 0.5em;
      width: 100%;
      height: 1px;
      content: "";
      transform: translateY(100%);
      background: currentColor;
    }
  }
}

.quantity {
  display: flex;
  flex-direction: column;
  gap: 8px;

  @include mobile(min) {
    flex-direction: row;
  }

  &Wrapper {
    grid-area: quantity;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    @include desktop-medium(min) {
      padding: 8px 24px 8px 0;
      gap: 8px;
    }
  }
}

.label {
  font-size: 10px;
  line-height: 16px;
  text-wrap: nowrap;

  @include desktop-medium(min) {
    font-size: 12px;
  }
}

.innerClassQuantity {
  width: 110px;
}

.creditInfo {
  &Wrapper {
    display: flex;
    align-items: start;
    flex-direction: column;
    justify-content: center;
    padding-right: 12px;
    grid-area: creditInfo;
    gap: 4px;

    @include tablet(min) {
      border-right: 1px dashed $color-dash-separator;
    }

    @include desktop-medium(min) {
      padding-right: 24px;
    }
  }

  &Tooltip {
    display: flex;
    flex-direction: row;
    gap: 4px;
    padding: 8px 0;

    &Ul {
      padding-inline-start: 15px;
    }
  }

  &Label {
    font-size: 10px;
    font-weight: $font-weight-medium-v2;
    line-height: 16px;
    padding-right: 4px;
    text-wrap: nowrap;
    text-decoration-line: underline;

    @include desktop-medium(min) {
      font-size: 12px;
    }

    &Wrapper {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      cursor: pointer;
    }
  }
}

.button {
  &Wrapper {
    grid-area: button;
    display: flex;
    align-items: center;
    justify-self: end;

    @include desktop-medium(min) {
      padding-right: 8px;
    }
  }
}

.quantityPriceWrapper {
  grid-area: quantityAndPrice;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;

  gap: 24px;

  @include desktop-small(min) {
    width: 344px;
    padding: 0 24px 0 0;
    border-right: 1px dashed $color-dash-separator;
  }

  @include desktop-medium(min) {
    width: 382px;
  }

  &WithoutBorder {
    border-right: unset;
    @include desktop-small(min) {
      width: 200px !important;
    }
  }

  &ReplacementItem {
    justify-content: end;

    @include tablet(min) {
      width: 168px;
    }
  }
}

.sticker {
  &Wrapper {
    grid-area: sticker;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;

    @include tablet(min) {
      padding-right: 12px;
    }

    @include desktop-medium(min) {
      padding-right: 24px;
      gap: 8px;
    }

    @include tablet(min) {
      border-right: 1px dashed $color-dash-separator;
    }
  }
}

.productBodyWrapper {
  display: flex;
  flex-direction: column;
  padding-top: 8px;
  border-top: 1px dashed $color-dash-separator;
  grid-area: productBody;
  grid-column: 1 / 3;

  @include tablet(min) {
    flex-direction: row;
    justify-content: end;
  }

  @include desktop-small(min) {
    padding-top: 0;
    border: unset;
    grid-column: unset;
  }
}

.stickerAndCreditWrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @include tablet(min) {
    padding-right: 12px;
  }

  @include desktop-medium(min) {
    padding-right: 24px;
  }
}

.hiddenHeading {
  display: none;
  width: 0;
  height: 0;
}

.infoIcon {
  color: $color-plum-500;
}
</style>
